import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import type {EventContainerTemplate as JourneysModuleConfiguration} from "~/domainModels/workspaceModules/eventContainersModule";
import {OttopilotEventContainerViewPanelSectionKind as OttopilotViewPanelSectionKind} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTemplateConfiguration";
import {
    AnankeClockIcon,
    AnankeLinkExternalIcon,
    AnankeModelVariantIcon,
    AnankePersonIcon,
    AnankeTagIcon,
} from "~/domainModels/workspaceSpecific/icons";
import {
    AMARON__EVENT_CONTAINER__AON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CAR_MAKE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CAR_MODEL__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_VOC__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_HOUSE_NO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ENQUIRIES_COUNT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FBCLID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FBRAID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__FUEL_TYPE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GBRAID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__GCLID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__INQUEUE_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LINK_ID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LINK_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__PREFERRED_CALLING_TIME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__QUOTED_PRICE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__REMARKS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SCHEDULED_CALL_DATE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SCHEDULED_CALL_TIME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SERVICE_COUNT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SERVICE_UPGRADE_REQUIRED__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SMARTPING_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_TERM__COLUMN_ID,
    AMARON_JOURNEY_NON_EDITABLE_LEAD_STATUS_GLOBAL_CONSTRAINTS,
} from "~/domainModels/workspaceSpecific/amaron";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uniqueUuid} from "@vesta/unique/unique";

function normalizeLabel(value: string): string {
    return value.trim().toLowerCase();
}

type ViewPanelColumn = {
    id: Uuid;
    label: string;
    columnId: Uuid | string;
    isAdditionalColumn: boolean;
    columnType: "OttopilotEventContainer";
};

type EditPanelColumn = ViewPanelColumn & {
    isEditable: boolean;
    isRequired?: boolean;
};

function createAmaronJourneyPanelHelpers(getAdditionalColumns: CapabilityUdoColumnResolver, journeysModuleConfiguration: JourneysModuleConfiguration) {
    const availableColumnIds = new Set<string>(
        getAdditionalColumns(journeysModuleConfiguration).map((column) => column.id),
    );
    const byDisplayName = new Map(
        getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]),
    );

    const getColumnId = (displayNames: string[], fallbackColumnIds: string[] = []): Uuid | null => {
        for (const id of fallbackColumnIds) {
            if (availableColumnIds.has(id)) {
                return id as Uuid;
            }
        }
        for (const displayName of displayNames) {
            const id = byDisplayName.get(normalizeLabel(displayName));
            if (id !== undefined) {
                return id;
            }
        }
        return null;
    };

    const toViewSectionColumns = (
        rows: Array<{
            label: string;
            displayNames: string[];
            fallbackColumnIds?: string[];
            fallbackSystemColumnId?: string;
        }>,
    ): Array<ViewPanelColumn> =>
        rows
            .map((row) => {
                const columnId = getColumnId(row.displayNames, row.fallbackColumnIds);
                if (columnId !== null) {
                    return {
                        id: columnId,
                        label: row.label,
                        columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }
                if (row.fallbackSystemColumnId !== undefined) {
                    return {
                        id: `amaron-journey-system-${row.fallbackSystemColumnId}-${normalizeLabel(row.label)}` as Uuid,
                        label: row.label,
                        columnId: row.fallbackSystemColumnId,
                        isAdditionalColumn: false,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }
                return null;
            })
            .filter((item): item is ViewPanelColumn => item !== null);

    const toEditSectionColumns = (
        rows: Array<{
            label: string;
            isEditable: boolean;
            isRequired?: boolean;
            displayNames: string[];
            fallbackColumnIds?: string[];
            fallbackSystemColumnId?: string;
        }>,
    ): Array<EditPanelColumn> =>
        rows
            .map((row) => {
                const columnId = getColumnId(row.displayNames, row.fallbackColumnIds);
                if (columnId !== null) {
                    const column: EditPanelColumn = {
                        id: columnId,
                        isEditable: row.isEditable,
                        label: row.label,
                        columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                    if (row.isRequired === true) {
                        column.isRequired = true;
                    }
                    return column;
                }
                if (row.fallbackSystemColumnId !== undefined) {
                    return {
                        id: `amaron-journey-edit-system-${row.fallbackSystemColumnId}-${normalizeLabel(row.label)}` as Uuid,
                        isEditable: false,
                        label: row.label,
                        columnId: row.fallbackSystemColumnId,
                        isAdditionalColumn: false,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }
                return null;
            })
            .filter((item): item is EditPanelColumn => item !== null);

    return {toViewSectionColumns, toEditSectionColumns};
}

const AMARON_CUSTOMER_DETAIL_ROWS: Array<{
    label: string;
    displayNames: string[];
    fallbackColumnIds?: string[];
    fallbackSystemColumnId?: string;
}> = [
    {label: "Priority", displayNames: ["Priority", "Source Priority", "source priority"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID]},
    {label: "Customer Name", displayNames: ["Customer Name", "customer name"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID]},
    {label: "Contact number", displayNames: ["Contact Number", "Contact number", "Phone Number"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID]},
    {label: "State", displayNames: ["Customer State", "State"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID]},
    {label: "City", displayNames: ["Customer City", "City"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID]},
    {label: "House No", displayNames: ["Customer House No", "House No", "House Number"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__CUSTOMER_HOUSE_NO__COLUMN_ID]},
    {label: "Preferred Calling Time", displayNames: ["Preferred Calling Time", "Preferred calling time", "preferred calling time"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__PREFERRED_CALLING_TIME__COLUMN_ID]},
    {label: "Quoted price", displayNames: ["Quoted price", "Quoted Price"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__QUOTED_PRICE__COLUMN_ID]},
    {label: "Service Upgrade Required", displayNames: ["Service Upgrade Required", "Service upgrade required", "service upgrade required"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__SERVICE_UPGRADE_REQUIRED__COLUMN_ID]},
];

const AMARON_PRODUCT_DETAIL_ROWS: Array<{
    label: string;
    displayNames: string[];
    fallbackColumnIds?: string[];
}> = [
    {label: "Fuel Type", displayNames: ["Fuel Type", "fuel type"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__FUEL_TYPE__COLUMN_ID]},
    {label: "Service Requested", displayNames: ["Service Requested", "service requested"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID]},
];

const AMARON_ASSIGNMENT_DETAIL_ROWS: Array<{
    label: string;
    displayNames: string[];
    fallbackColumnIds?: string[];
}> = [
    {label: "Assigned To", displayNames: ["Assigned To", "Assigned Agent", "assigned to"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID]},
    {label: "AON", displayNames: ["AON", "Action Owner", "Lead Owner"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__AON__COLUMN_ID]},
    {label: "In Queue Status", displayNames: ["In Queue Status", "Inqueue Status"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__INQUEUE_STATUS__COLUMN_ID]},
];

const AMARON_LEADS_ASSIGNMENT_DETAIL_ROWS: Array<{
    label: string;
    displayNames: string[];
    fallbackColumnIds?: string[];
}> = [
    {label: "Lead Status", displayNames: ["Lead Status", "lead status"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID]},
    {label: "Assigned To", displayNames: ["Assigned To", "Assigned Agent", "assigned to"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID]},
    {label: "AON", displayNames: ["AON", "Action Owner", "Lead Owner"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__AON__COLUMN_ID]},
    {label: "L1 Disposition", displayNames: ["L1 Disposition", "L1 disposition"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID]},
    {label: "L2 Disposition", displayNames: ["L2 Disposition", "L2 disposition"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID]},
    {label: "L3 Disposition", displayNames: ["L3 Disposition", "L3 disposition"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID]},
    {label: "L4 Disposition", displayNames: ["L4 Disposition", "L4 disposition"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID]},
    {label: "Remarks", displayNames: ["Remarks", "remarks"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__REMARKS__COLUMN_ID]},
];

const AMARON_ACTIVITY_DETAIL_ROWS: Array<{
    label: string;
    displayNames: string[];
    fallbackColumnIds?: string[];
    fallbackSystemColumnId?: string;
}> = [
    {label: "Scheduled Call Date", displayNames: ["Scheduled Call Date", "scheduled call date"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__SCHEDULED_CALL_DATE__COLUMN_ID]},
    {label: "Scheduled Call Time", displayNames: ["Scheduled Call Time", "scheduled call time"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__SCHEDULED_CALL_TIME__COLUMN_ID]},
    {label: "Last Activity", displayNames: ["Last Activity", "last activity"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID]},
    {label: "Last Activity On", displayNames: ["Last Activity On", "Last Activity Timestamp", "last activity on"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID]},
    {label: "Link Name", displayNames: ["Link Name", "link name"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__LINK_NAME__COLUMN_ID]},
    {label: "Link ID", displayNames: ["Link Id", "Link ID", "link id"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__LINK_ID__COLUMN_ID]},
    {label: "Assigned On", displayNames: ["Assigned On", "Assigned on"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID]},
    {label: "Created On", displayNames: ["Created On", "Created on"], fallbackSystemColumnId: "createdAt"},
    {label: "Modified By", displayNames: ["Modified By", "Updated By"], fallbackSystemColumnId: "updatedBy"},
    {label: "Modified On", displayNames: ["Modified On", "Updated On"], fallbackSystemColumnId: "updatedAt"},
    {label: "Enquiries Count", displayNames: ["Enquiries Count", "enquiries count"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__ENQUIRIES_COUNT__COLUMN_ID]},
    {label: "Calls Count", displayNames: ["Calls Count", "Call Count", "call count"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID]},
    {label: "Service Count", displayNames: ["Service Count", "service count"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__SERVICE_COUNT__COLUMN_ID]},
];

const AMARON_ACTIVITY_DETAIL_EDIT_ROWS = AMARON_ACTIVITY_DETAIL_ROWS.map((row) => ({
    ...row,
    isEditable: false,
}));

const AMARON_UTM_DETAIL_ROWS: Array<{
    label: string;
    displayNames: string[];
    fallbackColumnIds?: string[];
}> = [
    {label: "Smartping Campaign", displayNames: ["Smartping Campaign", "smartping campaign"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__SMARTPING_CAMPAIGN__COLUMN_ID]},
    {label: "Utm Platform", displayNames: ["Utm_platform", "utm_platform", "UTM Platform"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID]},
    {label: "Utm Source", displayNames: ["Utm_source", "utm_source", "UTM Source"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID]},
    {label: "Utm medium", displayNames: ["Utm_medium", "utm_medium", "UTM Medium"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__UTM_MEDIUM__COLUMN_ID]},
    {label: "Utm Campaign", displayNames: ["Utm_campaign", "utm_campaign", "UTM Campaign"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__UTM_CAMPAIGN__COLUMN_ID]},
    {label: "Utm Term", displayNames: ["Utm_term", "utm_term", "UTM Term"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__UTM_TERM__COLUMN_ID]},
    {label: "Utm Content", displayNames: ["Utm_content", "utm_content", "UTM Content"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__UTM_CONTENT__COLUMN_ID]},
    {label: "Google Ads Source", displayNames: ["Google Ads Source", "gadSource", "gad_source"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__GAD_SOURCE__COLUMN_ID]},
    {label: "Google Ads Campaign ID", displayNames: ["Google Ads Campaign ID", "gadCampaignId", "gad_campaignid"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__GAD_CAMPAIGN_ID__COLUMN_ID]},
    {label: "GBRAID", displayNames: ["GBRAID", "gbraid"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__GBRAID__COLUMN_ID]},
    {label: "GCLID", displayNames: ["GCLID", "gclid"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__GCLID__COLUMN_ID]},
    {label: "Facebook Ads Source", displayNames: ["Facebook Ads Source", "fadSource", "fad_source"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__FAD_SOURCE__COLUMN_ID]},
    {label: "Facebook Ads Campaign ID", displayNames: ["Facebook Ads Campaign ID", "fadCampaignId", "fad_campaignid"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__FAD_CAMPAIGN_ID__COLUMN_ID]},
    {label: "FBRAID", displayNames: ["FBRAID", "fbraid"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__FBRAID__COLUMN_ID]},
    {label: "FBCLID", displayNames: ["FBCLID", "fbclid"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__FBCLID__COLUMN_ID]},
];

const AMARON_UTM_DETAIL_EDIT_ROWS = AMARON_UTM_DETAIL_ROWS.map((row) => ({
    ...row,
    isEditable: false,
}));

const AMARON_DAY_PLAN_CUSTOMER_DETAIL_ROWS: Array<{
    label: string;
    displayNames: string[];
    fallbackColumnIds?: string[];
}> = [
    {label: "Priority", displayNames: ["Priority", "Source Priority", "source priority"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID]},
    {label: "Full name", displayNames: ["Customer Name", "customer name"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID]},
    {label: "Contact no.", displayNames: ["Contact Number", "Contact number", "Phone Number"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID]},
    {label: "City", displayNames: ["Customer City", "City"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID]},
    {label: "State", displayNames: ["Customer State", "State"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID]},
    {label: "House No.", displayNames: ["Customer House No", "House No", "House Number"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__CUSTOMER_HOUSE_NO__COLUMN_ID]},
    {label: "Preferred Calling Time", displayNames: ["Preferred Calling Time", "preferred calling time"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__PREFERRED_CALLING_TIME__COLUMN_ID]},
    {label: "Quoted price", displayNames: ["Quoted price", "Quoted Price"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__QUOTED_PRICE__COLUMN_ID]},
    {label: "Service Upgrade Required", displayNames: ["Service Upgrade Required", "service upgrade required"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__SERVICE_UPGRADE_REQUIRED__COLUMN_ID]},
];

const AMARON_DAY_PLAN_LEAD_DETAIL_ROWS: Array<{
    label: string;
    displayNames: string[];
    fallbackColumnIds?: string[];
    fallbackSystemColumnId?: string;
}> = [
    {label: "Lead Category", displayNames: ["Lead Category", "L1 Disposition", "L1 disposition"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID]},
    {label: "Current Disposition", displayNames: ["Disposition", "Current Disposition"], fallbackSystemColumnId: "disposition"},
    {label: "Assigned Agent", displayNames: ["Assigned To", "Assigned Agent", "assigned to"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID]},
    {label: "Last Activity", displayNames: ["Last Activity", "last activity"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID]},
    {label: "Last Activity Timestamp", displayNames: ["Last Activity Timestamp", "Last Activity On", "last activity on"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID]},
];

const AMARON_DAY_PLAN_VEHICLE_SERVICE_DETAIL_ROWS: Array<{
    label: string;
    displayNames: string[];
    fallbackColumnIds?: string[];
}> = [
    {label: "Car Model", displayNames: ["Car Model", "car model"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__CAR_MODEL__COLUMN_ID]},
    {label: "Fuel Type", displayNames: ["Fuel Type", "fuel type"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__FUEL_TYPE__COLUMN_ID]},
    {label: "Service Requested", displayNames: ["Service Requested", "service requested"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID]},
    {label: "Booking Status", displayNames: ["Booking Status", "Link Name", "link name"], fallbackColumnIds: [AMARON__EVENT_CONTAINER__LINK_NAME__COLUMN_ID]},
];

export function buildAmaronInQueueViewPanelConfiguration(getAdditionalColumns: CapabilityUdoColumnResolver,
    journeysModuleConfiguration: JourneysModuleConfiguration,
): JourneysModuleConfiguration["configuration"]["eventContainersPage"]["viewPanel"] {
    const {toViewSectionColumns} = createAmaronJourneyPanelHelpers(getAdditionalColumns, journeysModuleConfiguration);
    return {
        sections: [
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`amaron-inqueue-customer-details`,
                displayName: "Customer details",
                iconId: AnankePersonIcon,
                iconColor: "var(--ds-icon-accent-blue)",
                iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                initialIsOpen: true,
                columns: toViewSectionColumns(AMARON_CUSTOMER_DETAIL_ROWS),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`amaron-inqueue-product-details`,
                displayName: "Product details",
                iconId: AnankeModelVariantIcon,
                iconColor: "var(--ds-icon-accent-orange)",
                iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                initialIsOpen: true,
                columns: toViewSectionColumns(AMARON_PRODUCT_DETAIL_ROWS),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`amaron-inqueue-assignment-details`,
                displayName: "Assignment details",
                iconId: AnankeTagIcon,
                iconColor: "var(--ds-icon-accent-green)",
                iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                initialIsOpen: true,
                columns: toViewSectionColumns(AMARON_ASSIGNMENT_DETAIL_ROWS),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`amaron-inqueue-activity-details`,
                displayName: "Activity Details",
                iconId: AnankeClockIcon,
                iconColor: "var(--ds-icon-accent-yellow)",
                iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                initialIsOpen: true,
                columns: toViewSectionColumns(AMARON_ACTIVITY_DETAIL_ROWS),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`amaron-inqueue-utm-details`,
                displayName: "UTM Parameters",
                iconId: AnankeLinkExternalIcon,
                iconColor: "var(--ds-icon-accent-purple)",
                iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                initialIsOpen: true,
                columns: toViewSectionColumns(AMARON_UTM_DETAIL_ROWS),
            },
        ],
    };
}

export function buildAmaronLeadsViewPanelConfiguration(getAdditionalColumns: CapabilityUdoColumnResolver,
    journeysModuleConfiguration: JourneysModuleConfiguration,
): JourneysModuleConfiguration["configuration"]["eventContainersPage"]["viewPanel"] {
    const {toViewSectionColumns} = createAmaronJourneyPanelHelpers(getAdditionalColumns, journeysModuleConfiguration);
    return {
        sections: [
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`amaron-leads-customer-details`,
                displayName: "Customer details",
                iconId: AnankePersonIcon,
                iconColor: "var(--ds-icon-accent-blue)",
                iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                initialIsOpen: true,
                columns: toViewSectionColumns(AMARON_CUSTOMER_DETAIL_ROWS),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`amaron-leads-product-details`,
                displayName: "Product details",
                iconId: AnankeModelVariantIcon,
                iconColor: "var(--ds-icon-accent-orange)",
                iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                initialIsOpen: true,
                columns: toViewSectionColumns(AMARON_PRODUCT_DETAIL_ROWS),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`amaron-leads-assignment-details`,
                displayName: "Lead details",
                iconId: AnankeTagIcon,
                iconColor: "var(--ds-icon-accent-green)",
                iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                initialIsOpen: true,
                columns: toViewSectionColumns(AMARON_LEADS_ASSIGNMENT_DETAIL_ROWS),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`amaron-leads-activity-details`,
                displayName: "Activity Details",
                iconId: AnankeClockIcon,
                iconColor: "var(--ds-icon-accent-yellow)",
                iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                initialIsOpen: true,
                columns: toViewSectionColumns(AMARON_ACTIVITY_DETAIL_ROWS),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`amaron-leads-utm-details`,
                displayName: "UTM Parameters",
                iconId: AnankeLinkExternalIcon,
                iconColor: "var(--ds-icon-accent-purple)",
                iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                initialIsOpen: true,
                columns: toViewSectionColumns(AMARON_UTM_DETAIL_ROWS),
            },
        ],
    };
}

/** My Day Plan view panel: calling-agent detail sections. */
export function buildAmaronDayPlanViewPanelConfiguration(getAdditionalColumns: CapabilityUdoColumnResolver,
    journeysModuleConfiguration: JourneysModuleConfiguration,
): JourneysModuleConfiguration["configuration"]["eventContainersPage"]["viewPanel"] {
    const {toViewSectionColumns} = createAmaronJourneyPanelHelpers(getAdditionalColumns, journeysModuleConfiguration);
    return {
        sections: [
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`amaron-day-plan-customer-details`,
                displayName: "Customer Details",
                iconId: AnankePersonIcon,
                iconColor: "var(--ds-icon-accent-blue)",
                iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                initialIsOpen: true,
                columns: toViewSectionColumns(AMARON_DAY_PLAN_CUSTOMER_DETAIL_ROWS),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`amaron-day-plan-lead-details`,
                displayName: "Lead Details",
                iconId: AnankeTagIcon,
                iconColor: "var(--ds-icon-accent-green)",
                iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                initialIsOpen: true,
                columns: toViewSectionColumns(AMARON_DAY_PLAN_LEAD_DETAIL_ROWS),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`amaron-day-plan-vehicle-service-details`,
                displayName: "Vehicle & Service Details",
                iconId: AnankeModelVariantIcon,
                iconColor: "var(--ds-icon-accent-orange)",
                iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                initialIsOpen: true,
                columns: toViewSectionColumns(AMARON_DAY_PLAN_VEHICLE_SERVICE_DETAIL_ROWS),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`amaron-day-plan-utm-details`,
                displayName: "UTM Parameter",
                iconId: AnankeLinkExternalIcon,
                iconColor: "var(--ds-icon-accent-purple)",
                iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                initialIsOpen: true,
                columns: toViewSectionColumns(AMARON_UTM_DETAIL_ROWS),
            },
        ],
    };
}

/** My Day Plan edit panel: editable journey fields only. */
export function buildAmaronDayPlanEditPanelConfiguration(getAdditionalColumns: CapabilityUdoColumnResolver,
    journeysModuleConfiguration: JourneysModuleConfiguration,
): JourneysModuleConfiguration["configuration"]["eventContainersPage"]["editPanel"] {
    const {toEditSectionColumns} = createAmaronJourneyPanelHelpers(getAdditionalColumns, journeysModuleConfiguration);
    const editable = (label: string, displayNames: string[], fallbackColumnIds?: string[]) => ({
        label,
        isEditable: true,
        displayNames,
        fallbackColumnIds,
    });
    const requiredEditable = (label: string, displayNames: string[], fallbackColumnIds?: string[]) => ({
        label,
        isEditable: true,
        isRequired: true,
        displayNames,
        fallbackColumnIds,
    });

    return {
        columnGrouping: [
            {
                id: uniqueUuid`amaron-day-plan-edit-customer-details`,
                displayName: "Customer Details",
                iconId: AnankePersonIcon,
                iconColor: "var(--ds-icon-accent-blue)",
                iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                initialIsOpen: true,
                columns: toEditSectionColumns([
                    requiredEditable("Full name", ["Customer Name", "customer name"], [AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID]),
                    editable("City", ["Customer City", "City"], [AMARON__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID]),
                    editable("State", ["Customer State", "State"], [AMARON__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID]),
                    editable("House No.", ["Customer House No", "House No", "House Number"], [AMARON__EVENT_CONTAINER__CUSTOMER_HOUSE_NO__COLUMN_ID]),
                    editable("Preferred Calling Time", ["Preferred Calling Time", "preferred calling time"], [AMARON__EVENT_CONTAINER__PREFERRED_CALLING_TIME__COLUMN_ID]),
                    editable("Quoted price", ["Quoted price", "Quoted Price"], [AMARON__EVENT_CONTAINER__QUOTED_PRICE__COLUMN_ID]),
                    editable("Service Upgrade Required", ["Service Upgrade Required", "service upgrade required"], [AMARON__EVENT_CONTAINER__SERVICE_UPGRADE_REQUIRED__COLUMN_ID]),
                ]),
            },
            {
                id: uniqueUuid`amaron-day-plan-edit-lead-details`,
                displayName: "Lead Details",
                iconId: AnankeTagIcon,
                iconColor: "var(--ds-icon-accent-green)",
                iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                initialIsOpen: true,
                columns: toEditSectionColumns([
                    editable("Assigned Agent", ["Assigned To", "Assigned Agent", "assigned to"], [AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID]),
                ]),
            },
            {
                id: uniqueUuid`amaron-day-plan-edit-vehicle-service-details`,
                displayName: "Vehicle & Service Details",
                iconId: AnankeModelVariantIcon,
                iconColor: "var(--ds-icon-accent-orange)",
                iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                initialIsOpen: true,
                columns: toEditSectionColumns([
                    editable("Car Model", ["Car Model", "car model"], [AMARON__EVENT_CONTAINER__CAR_MODEL__COLUMN_ID]),
                    editable("Fuel Type", ["Fuel Type", "fuel type"], [AMARON__EVENT_CONTAINER__FUEL_TYPE__COLUMN_ID]),
                    editable("Service Requested", ["Service Requested", "service requested"], [AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID]),
                ]),
            },
            {
                id: uniqueUuid`amaron-day-plan-edit-utm-details`,
                displayName: "UTM Parameters",
                iconId: AnankeLinkExternalIcon,
                iconColor: "var(--ds-icon-accent-purple)",
                iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                initialIsOpen: true,
                columns: toEditSectionColumns(AMARON_UTM_DETAIL_EDIT_ROWS),
            },
        ],
        globalConstraints: AMARON_JOURNEY_NON_EDITABLE_LEAD_STATUS_GLOBAL_CONSTRAINTS,
    };
}

export function buildAmaronInQueueEditPanelConfiguration(getAdditionalColumns: CapabilityUdoColumnResolver,
    journeysModuleConfiguration: JourneysModuleConfiguration,
): JourneysModuleConfiguration["configuration"]["eventContainersPage"]["editPanel"] {
    const {toEditSectionColumns} = createAmaronJourneyPanelHelpers(getAdditionalColumns, journeysModuleConfiguration);
    const editable = (label: string, displayNames: string[], fallbackColumnIds?: string[]) => ({
        label,
        isEditable: true,
        displayNames,
        fallbackColumnIds,
    });
    const readOnly = (label: string, displayNames: string[], fallbackColumnIds?: string[]) => ({
        label,
        isEditable: false,
        displayNames,
        fallbackColumnIds,
    });
    return {
        columnGrouping: [
            {
                id: uniqueUuid`amaron-inqueue-edit-customer-details`,
                displayName: "Customer details",
                iconId: AnankePersonIcon,
                iconColor: "var(--ds-icon-accent-blue)",
                iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                initialIsOpen: true,
                columns: toEditSectionColumns([
                    editable("Customer Name", ["Customer Name", "customer name"], [AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID]),
                    readOnly("Contact number", ["Contact Number", "Contact number", "Phone Number"], [AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID]),
                    readOnly("State", ["Customer State", "State"], [AMARON__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID]),
                    readOnly("City", ["Customer City", "City"], [AMARON__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID]),
                    readOnly("House No", ["Customer House No", "House No", "House Number"], [AMARON__EVENT_CONTAINER__CUSTOMER_HOUSE_NO__COLUMN_ID]),
                ]),
            },
            {
                id: uniqueUuid`amaron-inqueue-edit-product-details`,
                displayName: "Product details",
                iconId: AnankeModelVariantIcon,
                iconColor: "var(--ds-icon-accent-orange)",
                iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                initialIsOpen: true,
                columns: toEditSectionColumns([
                    readOnly("Fuel Type", ["Fuel Type", "fuel type"], [AMARON__EVENT_CONTAINER__FUEL_TYPE__COLUMN_ID]),
                    readOnly("Service Requested", ["Service Requested", "service requested"], [AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID]),
                ]),
            },
            {
                id: uniqueUuid`amaron-inqueue-edit-assignment-details`,
                displayName: "Assignment details",
                iconId: AnankeTagIcon,
                iconColor: "var(--ds-icon-accent-green)",
                iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                initialIsOpen: true,
                columns: toEditSectionColumns([
                    editable("Assigned Agent", ["Assigned To", "Assigned Agent", "assigned to"], [AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID]),
                    readOnly("AON", ["AON", "Action Owner", "Lead Owner"], [AMARON__EVENT_CONTAINER__AON__COLUMN_ID]),
                    readOnly("In Queue Status", ["In Queue Status", "Inqueue Status"], [AMARON__EVENT_CONTAINER__INQUEUE_STATUS__COLUMN_ID]),
                ]),
            },
            {
                id: uniqueUuid`amaron-inqueue-edit-activity-details`,
                displayName: "Activity Details",
                iconId: AnankeClockIcon,
                iconColor: "var(--ds-icon-accent-yellow)",
                iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                initialIsOpen: true,
                columns: toEditSectionColumns(AMARON_ACTIVITY_DETAIL_EDIT_ROWS),
            },
            {
                id: uniqueUuid`amaron-inqueue-edit-utm-details`,
                displayName: "UTM Parameters",
                iconId: AnankeLinkExternalIcon,
                iconColor: "var(--ds-icon-accent-purple)",
                iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                initialIsOpen: true,
                columns: toEditSectionColumns(AMARON_UTM_DETAIL_EDIT_ROWS),
            },
        ],
    };
}

export function buildAmaronLeadsEditPanelConfiguration(getAdditionalColumns: CapabilityUdoColumnResolver,
    journeysModuleConfiguration: JourneysModuleConfiguration,
): JourneysModuleConfiguration["configuration"]["eventContainersPage"]["editPanel"] | undefined {
    const existingEditPanel =
        journeysModuleConfiguration.configuration.eventContainersPage.editPanel;
    if (existingEditPanel === undefined) {
        return undefined;
    }
    const {toEditSectionColumns} = createAmaronJourneyPanelHelpers(getAdditionalColumns, journeysModuleConfiguration);
    const editable = (label: string, displayNames: string[], fallbackColumnIds?: string[]) => ({
        label,
        isEditable: true,
        displayNames,
        fallbackColumnIds,
    });
    const requiredEditable = (label: string, displayNames: string[], fallbackColumnIds?: string[]) => ({
        label,
        isEditable: true,
        isRequired: true,
        displayNames,
        fallbackColumnIds,
    });
    const readOnly = (label: string, displayNames: string[], fallbackColumnIds?: string[]) => ({
        label,
        isEditable: false,
        displayNames,
        fallbackColumnIds,
    });
    return {
        columnGrouping: [
            {
                id: uniqueUuid`amaron-leads-edit-customer-details`,
                displayName: "Customer details",
                iconId: AnankePersonIcon,
                iconColor: "var(--ds-icon-accent-blue)",
                iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                initialIsOpen: true,
                columns: toEditSectionColumns([
                    requiredEditable("Customer Name", ["Customer Name", "customer name"], [AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID]),
                    readOnly("Contact number", ["Contact Number", "Contact number", "Phone Number"], [AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID]),
                    editable("Customer State", ["Customer State", "State"], [AMARON__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID]),
                    editable("Customer City", ["Customer City", "City"], [AMARON__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID]),
                    editable("Customer House No.", ["Customer House No", "House No", "House Number"], [AMARON__EVENT_CONTAINER__CUSTOMER_HOUSE_NO__COLUMN_ID]),
                    editable("Customer VOC", ["Customer VOC", "customer voc"], [AMARON__EVENT_CONTAINER__CUSTOMER_VOC__COLUMN_ID]),
                    editable("Preferred Calling Time", ["Preferred Calling Time", "Preferred calling time", "preferred calling time"], [AMARON__EVENT_CONTAINER__PREFERRED_CALLING_TIME__COLUMN_ID]),
                    editable("Quoted price", ["Quoted price", "Quoted Price"], [AMARON__EVENT_CONTAINER__QUOTED_PRICE__COLUMN_ID]),
                    editable("Service Upgrade Required", ["Service Upgrade Required", "Service upgrade required", "service upgrade required"], [AMARON__EVENT_CONTAINER__SERVICE_UPGRADE_REQUIRED__COLUMN_ID]),
                ]),
            },
            {
                id: uniqueUuid`amaron-leads-edit-product-details`,
                displayName: "Product details",
                iconId: AnankeModelVariantIcon,
                iconColor: "var(--ds-icon-accent-orange)",
                iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                initialIsOpen: true,
                columns: toEditSectionColumns([
                    editable("Car Make", ["Car Make", "car make"], [AMARON__EVENT_CONTAINER__CAR_MAKE__COLUMN_ID]),
                    editable("Car Model", ["Car Model", "car model"], [AMARON__EVENT_CONTAINER__CAR_MODEL__COLUMN_ID]),
                    editable("Fuel Type", ["Fuel Type", "fuel type"], [AMARON__EVENT_CONTAINER__FUEL_TYPE__COLUMN_ID]),
                    editable("Service Requested", ["Service Requested", "service requested"], [AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID]),
                ]),
            },
            {
                id: uniqueUuid`amaron-leads-edit-assignment-details`,
                displayName: "Lead details",
                iconId: AnankeTagIcon,
                iconColor: "var(--ds-icon-accent-green)",
                iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                initialIsOpen: true,
                columns: toEditSectionColumns([
                    readOnly("Lead Status", ["Lead Status", "lead status"], [AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID]),
                    editable("Assigned Agent", ["Assigned To", "Assigned Agent", "assigned to"], [AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID]),
                    readOnly("AON", ["AON", "Action Owner", "Lead Owner"], [AMARON__EVENT_CONTAINER__AON__COLUMN_ID]),
                    readOnly("L1 Disposition", ["L1 Disposition", "L1 disposition"], [AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID]),
                    readOnly("L2 Disposition", ["L2 Disposition", "L2 disposition"], [AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID]),
                    readOnly("L3 Disposition", ["L3 Disposition", "L3 disposition"], [AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID]),
                    readOnly("L4 Disposition", ["L4 Disposition", "L4 disposition"], [AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID]),
                    readOnly("Remarks", ["Remarks", "remarks"], [AMARON__EVENT_CONTAINER__REMARKS__COLUMN_ID]),
                ]),
            },
            {
                id: uniqueUuid`amaron-leads-edit-activity-details`,
                displayName: "Activity Details",
                iconId: AnankeClockIcon,
                iconColor: "var(--ds-icon-accent-yellow)",
                iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                initialIsOpen: true,
                columns: toEditSectionColumns(AMARON_ACTIVITY_DETAIL_EDIT_ROWS),
            },
            {
                id: uniqueUuid`amaron-leads-edit-utm-details`,
                displayName: "UTM Parameters",
                iconId: AnankeLinkExternalIcon,
                iconColor: "var(--ds-icon-accent-purple)",
                iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                initialIsOpen: true,
                columns: toEditSectionColumns(AMARON_UTM_DETAIL_EDIT_ROWS),
            },
        ],
    };
}
